import { Injectable, Inject, Renderer2, RendererFactory2, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

declare global { interface Window { fbq?: any; } }

@Injectable({ providedIn: 'root' })
export class FbPixelService {
  private renderer: Renderer2;
  private routerSub?: Subscription;
  private initialized = false;

  constructor(
    private rendererFactory: RendererFactory2,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!environment.fbPixel?.enabled) return;
    if (this.initialized) return;

    const id = environment.fbPixel.id;
    if (!id) return;

    // don't double-insert
    if (this.document.getElementById('fb-pixel-script')) {
      this.initialized = true;
      this.attachRouterTracking();
      return;
    }

    // standard fbq snippet as one inline script (safe because we're in browser)
    const snippet = `(function(f,b,e,v,n,t,s){if(f.fbq) return; n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments): n.queue.push(arguments)}; if(!f._fbq) f._fbq=n; n.push=n; n.loaded=true; n.version='2.0'; n.queue=[]; t=b.createElement(e); t.async=true; t.src='https://connect.facebook.net/en_US/fbevents.js'; s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s); })(window, document, 'script'); fbq('init','${id}'); fbq('track','PageView');`;

    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'id', 'fb-pixel-script');
    script.type = 'text/javascript';
    // set text content
    (script as HTMLScriptElement).text = snippet;
    this.renderer.appendChild(this.document.head, script);

    // noscript fallback
    const noscript = this.renderer.createElement('noscript');
    this.renderer.setAttribute(noscript, 'id', 'fb-pixel-noscript');
    
    // Create img element safely using Renderer2
    const img = this.renderer.createElement('img');
    this.renderer.setAttribute(img, 'height', '1');
    this.renderer.setAttribute(img, 'width', '1');
    this.renderer.setAttribute(img, 'style', 'display:none');
    this.renderer.setAttribute(img, 'src', `https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`);
    
    this.renderer.appendChild(noscript, img);
    this.renderer.appendChild(this.document.body, noscript);

    this.initialized = true;
    this.attachRouterTracking();
  }

  private attachRouterTracking() {
    if (this.routerSub) return;
    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => {
      try {
        window.fbq && window.fbq('track', 'PageView');
      } catch (err) {
        // swallow errors
      }
    });
  }

  track(event: string, params?: Record<string, any>) {
    if (!isPlatformBrowser(this.platformId) || !environment.fbPixel?.enabled) return;
    try {
      window.fbq && window.fbq('track', event, params || {});
    } catch (err) {
      console.error('FB Pixel track error', err);
    }
  }

  // optional: call this when user gives consent
  enableAfterConsent() { this.init(); }

  // optional cleanup (if ever required)
  destroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
      this.routerSub = undefined;
    }
    const s = this.document.getElementById('fb-pixel-script');
    if (s) s.remove();
    const n = this.document.getElementById('fb-pixel-noscript');
    if (n) n.remove();
    this.initialized = false;
    try { delete window.fbq; } catch {}
  }
}
